import {CurrentItem} from "./CurrentItem.js";
import {cookies, router} from "@intermesh/goui";
import {ServerApi} from "../util/ServerApi.js";
import {PortalConfig} from "../config.js";
import {t, Translate} from "../util/Translate.js";
import {IfwWindow} from "../components/IfwWindow.js";
import {ForcePasswordChangeDialog} from "../ForcePasswordChangeDialog.js";
import {User} from "../model/User.js";

declare global {
	var Android: any;
}

export class CurrentUser extends CurrentItem {
	public static userId?: number;
	static storageItem = "currentUser";

	/**
	 * Returns the validated Eva portal URL from the session, or null when missing or invalid.
	 */
	static evaPortalUrl(): string | null {
		const evaPortal = sessionStorage.getItem("evaPortal")?.trim();
		if (!evaPortal || !/^(?!-)[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*$/.test(evaPortal)) {
			return null;
		}
		try {
			const url = new URL(`https://${evaPortal}.bezoekersregistratie.net`);
			return url.protocol === "https:" && url.hostname.endsWith(".bezoekersregistratie.net") ? url.origin : null;
		} catch (e) {
			return null;
		}
	}
	static baseRoute = "elearning/auth";
	static returnProperties = "*,user[*]";

	private static idleTimeoutId: number | null = null;
	private static readonly idleEvents = ['pointerdown', 'pointermove', 'pointerup', 'keydown'];
	private static readonly idleHandler = () => CurrentUser.resetIdleTimer();
	private static readonly idleTimeMs = 2 * 60 * 1000;


	public static async readIf(): Promise<User | undefined> {
		if (!sessionStorage.getItem(CurrentUser.storageItem)) {
			const url = ServerApi.url(CurrentUser.baseRoute, {returnProperties: CurrentUser.returnProperties});
			const response = await ServerApi.call(url);
			const pc = {
				...PortalConfig, ...{
					title: response.portalName,
					forceUniqueEmailAddress: response.forceUniqueEmailAddress,
					generateCredentials: response.generateCredentials,
					mayRegister: response.mayRegister,
					mayUseOauth: response.mayUseOauth,
					mayUseSignin: response.mayUseSignin,
					mayUseEva: response.mayUseEva,
					mustAcceptTerms: response.mustAcceptTerms,
					notificationCount: response.notificationCount,
					showBirthDate: response.showBirthDate,
					requireBirthDate: response.requireBirthDate,
					openCourseSelection: response.openCourseSelection,mayRepeatCourse: response.mayRepeatCourse,
					enableRemindersByDefault: response.enableRemindersByDefault,
					defaultLogoBlob: response.defaultLogoBlob,
					defaultBackgroundBlob: response.defaultBackgroundBlob,
					activePasswordRuleSet: response.activePasswordRuleSet,
					expiresAt: Date.now() + (24 * 60 * 60 * 1000)
				}
			};

			// pc.defaultSectionId = pc.defaultSectionId || response.defaultSectionId;

			localStorage.setItem("portalConfig", JSON.stringify(pc));

			// User appears to be logged in.
			if (response.success) {
				sessionStorage.removeItem("preventRedirectToStart");
				if (response.data.user) {
					sessionStorage.setItem(CurrentUser.storageItem, JSON.stringify(response.data.user));
					return CurrentUser.get();
				}
			} else {
				// This is an invalid accessToken cookie, so we remove it.
				if (cookies.get("accessToken")) {
					await CurrentUser.logout();
					return undefined;
				}
			}
		}

		return CurrentUser.get();
	}

	public static get(): User | undefined {
		if (sessionStorage.getItem(CurrentUser.storageItem)) {
			return JSON.parse(sessionStorage.getItem(CurrentUser.storageItem)!);
		}
		return undefined;
	}

	/**
	 * The user logs out or is being logged out. Clean out all the shit.
	 *
	 * @TODO better session handling. Not sure whether logging out on the index page is still needed. See #1105
	 * @param keepTokens bool|null|undefined
	 */
	public static logout(keepTokens: boolean | null | undefined = false) {
		Translate.clear();

		const portalConfig = JSON.parse(localStorage.getItem("portalConfig")!);
		let evaRedirectUrl:string | null = null;

		if (portalConfig && portalConfig.mayUseEva) {
			let evaPortal = sessionStorage.getItem("evaPortal");
			if (evaPortal) {
				evaPortal = evaPortal.trim();

				if (/^(?!-)[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*$/.test(evaPortal)) {
					evaRedirectUrl = `https://${evaPortal}.bezoekersregistratie.net`;

					try {
						const url = new URL(evaRedirectUrl);
						if (url.protocol !== "https:") {
							evaRedirectUrl = null;
						}
					} catch (e) {
						evaRedirectUrl = null;
					}
				}
			}
		}

		if (!cookies.get("accessToken") && !evaRedirectUrl) {
			// TODO? Just keep a few relevant keys like invitationToken?
			if (!keepTokens) {
				sessionStorage.clear();
			}
			void router.goto("");

			return;
		}
		const url = ServerApi.url('auth');

		return ServerApi.call(url, {}, "DELETE").then((_response) => {
			const preventRedirect = sessionStorage.getItem("preventRedirectToStart");
			sessionStorage.clear();
			cookies.unset("accessToken", "/");
			// Github #533: some tablet devices use a non-standard browser. Instead of letting the owners choose
			// their own well-supported browsers, this was the 'solution' they came up with.
			if (typeof Android !== "undefined" && Android.restart) {
				// Restart the browser at the starting URL - for the IP Digital Signage platform only
				Android.restart();
			} else if (preventRedirect) {
				// In some cases, do NOT go back to start, e.g. when checking for existing authentications
				return;
			} else {
				// The OOTB supported Non-retarded way
				// Also make sure that any GET parameters are forgotten as well
				// let url = document.location.href;
				// url = url.replace(document.location.search, "");
				// location.replace(url);
				// void router.goto("");
				if (evaRedirectUrl) {
					window.location.href = evaRedirectUrl;
				} else {
					document.location = PortalConfig.frontendUrl;
				}
			}

		}).catch((error) => {
			// User tries to log out using an already invalid token. We simply remove the cookie and move on
			cookies.unset("accessToken", "/");
			document.location = PortalConfig.frontendUrl;
		});
	}

	public static async confirmLogout() {
		const confirm = await IfwWindow.confirm(t('Are you sure you want to logout?'), t('Confirm logout'));
		if (confirm) {
			CurrentUser.logout();
		}
	}

	public static forcePasswordChangePopupCheck(password: string | undefined) {
		const cu = CurrentUser.get();
		if (cu!.forcePasswordChange) {
			if (!password) {
				CurrentUser.logout();
			}
			const fpc = new ForcePasswordChangeDialog({
				password: password
			});
			fpc.show();
		}
	}

	/**
	 * Check whether the currently logged in user has module access. The default permission type is 'read'
	 *
	 * @param moduleName
	 * @param packageName
	 * @param permission
	 */
	public static hasModule(moduleName: string, packageName = "Instructiefilm", permission = "read") {
		const cu = CurrentUser.get();

		if (!cu) {
			return false;
		}

		const userModule = cu.modules.find((m: any): boolean => {
			return m.name === "GO\\Modules\\" + packageName + "\\" + moduleName + "\\Module";
		});
		if (!userModule) {
			return false;
		}
		return userModule.permissions[permission];
	}

	public static enableIdleLogout() {
		CurrentUser.idleEvents.forEach(event => {
			window.addEventListener(event, CurrentUser.idleHandler, {passive: true});
		});
		CurrentUser.resetIdleTimer();
	}

	public static disableIdleLogout() {
		CurrentUser.idleEvents.forEach(event => {
			window.removeEventListener(event, CurrentUser.idleHandler);
		});
		if (CurrentUser.idleTimeoutId !== null) {
			clearTimeout(CurrentUser.idleTimeoutId);
			CurrentUser.idleTimeoutId = null;
		}
	}

	private static resetIdleTimer() {
		if (CurrentUser.idleTimeoutId !== null) {
			clearTimeout(CurrentUser.idleTimeoutId);
		}
		CurrentUser.idleTimeoutId = window.setTimeout(() => {
			CurrentUser.logout(true);
		}, CurrentUser.idleTimeMs);
	}
}

export const currentUser = () => Object.assign(new CurrentUser());